import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Switch, { SwitchClickHandler } from '@splunk/react-ui/Switch';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../../../common/theme/ThemeProvider';
import P from '@splunk/react-ui/Paragraph';
import { EntityTypeSelect } from '@splunk/olly-imm';
import { isEqual } from 'lodash';
import { useEntityTypes } from '@splunk/olly-imm/build/entity/hooks/useEntityTypes';
import { EntityTypeMatchCategory } from '@splunk/olly-imm/build/entity/model/EntityType';
import { variables } from '@splunk/themes';

interface EntityHealthContentProps {
    entityType?: string;
    isNewDetector: boolean;
    onChange: (entityType: string | undefined) => void;
    dimensionsMap?: Map<string, Set<string>>;
}

const Container = styled.div`
    padding: 8px;
`;

const EntityContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: ${variables.spacingSmall};
`;

const EntityHealthContent: React.FC<EntityHealthContentProps> = ({
    entityType,
    isNewDetector,
    onChange,
    dimensionsMap,
}): JSX.Element => {
    const [currentEntityType, setCurrentEntityType] = useState(entityType);
    const [attachToEntityHealth, setAttachToEntityHealth] = useState<boolean>(!!currentEntityType);
    const [dimensionsMapChanged, setDimensionsMapChanged] = useState(false);

    const prevCount = useRef<Map<string, Set<string>>>();

    const themeKey = useLegacyThemeServiceKey();

    const onEntityChange = (newValue: string | undefined): void => {
        setCurrentEntityType(newValue);
        onChange(newValue);
    };

    useEffect(() => {
        setCurrentEntityType(entityType);
        setAttachToEntityHealth(!!entityType);
    }, [entityType]);

    useEffect(() => {
        if (!isEqual(prevCount.current, dimensionsMap)) {
            const isNotInitialLoad = !!prevCount.current && prevCount.current.size > 0;
            prevCount.current = dimensionsMap;

            if (isNotInitialLoad) {
                setDimensionsMapChanged((prev) => !prev);
            }
        }
    }, [dimensionsMap]);

    const dimensions = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        const allDims = new Set<string>();
        dimensionsMap.forEach((dims) => {
            dims.forEach((dim) => allDims.add(dim));
        });
        return Array.from(allDims);
    }, [dimensionsMap]);

    const metrics = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        return Array.from(dimensionsMap.keys());
    }, [dimensionsMap]);

    const { entityTypes, isLoading } = useEntityTypes(
        dimensions.length > 0 || metrics.length > 0 ? { dimensions, metrics } : undefined
    );

    useEffect(() => {
        if ((isNewDetector || dimensionsMapChanged) && !isLoading) {
            const preferredEntity = entityTypes.find(
                (et) => et.matchCategory === EntityTypeMatchCategory.PREFERRED
            );

            if (preferredEntity?.identifier !== currentEntityType) {
                setAttachToEntityHealth(true);
                onChange(preferredEntity ? preferredEntity.identifier : undefined);
            }
            setDimensionsMapChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewDetector, entityTypes, isLoading, onChange, dimensionsMapChanged]);

    const handleToggleValue: SwitchClickHandler = (): void => {
        if (attachToEntityHealth) {
            setAttachToEntityHealth(false);
            onEntityChange(undefined);
        } else {
            const preferredEntity = entityTypes.find(
                (et) => et.matchCategory === EntityTypeMatchCategory.PREFERRED
            );
            if (preferredEntity) {
                onEntityChange(preferredEntity.identifier);
            }
            setAttachToEntityHealth(true);
        }
    };

    return (
        <ThemeProvider colorScheme={themeKey}>
            <Container>
                <P>
                    The signals selected will influence the type of entity suggestions offered
                    below. Attaching a detector to an entity&#39;s health means it will affect the
                    health of a targeted entity, such as a service.
                </P>

                <EntityContainer>
                    <Switch
                        selected={attachToEntityHealth}
                        onClick={handleToggleValue}
                        appearance="toggle"
                    >
                        Attach detector to entity health
                    </Switch>

                    {attachToEntityHealth && entityTypes.length > 0 && (
                        <EntityTypeSelect
                            onChange={onEntityChange}
                            value={currentEntityType}
                            entityTypes={entityTypes}
                        ></EntityTypeSelect>
                    )}
                </EntityContainer>
            </Container>
        </ThemeProvider>
    );
};

export default EntityHealthContent;
